import React from 'react';
import { 
  Card, 
  CardHeader, 
  CardContent, 
  Divider, 
  List, 
  ListItem, 
  ListItemText, 
  ListItemAvatar, 
  Avatar, 
  Chip, 
  Typography, 
  Button, 
  Box, 
  Skeleton
} from '@mui/material';
import PaymentIcon from '@mui/icons-material/Payment';
import MoneyIcon from '@mui/icons-material/Money';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import { useNavigate } from 'react-router-dom';

/**
 * RecentPayments Component
 * Displays a card with the most recent payments
 */
const RecentPayments = ({ payments = [], loading = false }) => {
  const navigate = useNavigate();
  
  // Get icon based on payment method
  const getPaymentIcon = (method) => {
    switch (method) {
      case 'creditCard':
        return <CreditCardIcon />;
      case 'bankTransfer':
        return <AccountBalanceIcon />;
      case 'interac':
        return <CompareArrowsIcon />;
      case 'wireTransfer':
        return <MoneyIcon />;
      default:
        return <PaymentIcon />;
    }
  };
  
  // Get color based on payment status
  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed':
        return 'success';
      case 'pending':
        return 'warning';
      case 'failed':
        return 'error';
      default:
        return 'default';
    }
  };
  
  // Format payment method for display
  const formatPaymentMethod = (method) => {
    switch (method) {
      case 'creditCard':
        return 'Credit Card';
      case 'bankTransfer':
        return 'Bank Transfer';
      case 'interac':
        return 'Interac E-Transfer';
      case 'wireTransfer':
        return 'Wire Transfer';
      default:
        return method;
    }
  };
  
  // Format currency
  const formatCurrency = (amount, currency) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD'
    }).format(amount);
  };
  
  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    }).format(date);
  };
  
  return (
    <Card>
      <CardHeader 
        title="Recent Payments" 
        action={
          <Button 
            color="primary" 
            onClick={() => navigate('/admin/payments')}
            disabled={loading}
          >
            View All
          </Button>
        }
      />
      <Divider />
      <CardContent sx={{ p: 0 }}>
        {loading ? (
          <List>
            {[1, 2, 3].map((_, index) => (
              <ListItem key={index} divider={index < 2}>
                <ListItemAvatar>
                  <Skeleton variant="circular" width={40} height={40} />
                </ListItemAvatar>
                <ListItemText
                  primary={<Skeleton width="60%" />}
                  secondary={<Skeleton width="40%" />}
                />
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                  <Skeleton width={80} height={32} />
                  <Skeleton width={60} />
                </Box>
              </ListItem>
            ))}
          </List>
        ) : payments.length === 0 ? (
          <List>
            <ListItem>
              <ListItemText 
                primary="No recent payments" 
                secondary="Payments will appear here when customers complete checkout"
              />
            </ListItem>
          </List>
        ) : (
          <List>
            {payments.map((payment, index) => (
              <ListItem 
                key={payment._id || index} 
                divider={index < payments.length - 1}
                button
                onClick={() => navigate(`/admin/payments/${payment._id}`)}
              >
                <ListItemAvatar>
                  <Avatar sx={{ bgcolor: 'primary.main' }}>
                    {getPaymentIcon(payment.paymentMethod)}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    // Handle both nested customer object and populated customerId field
                    (payment.customer?.firstName && payment.customer?.lastName
                      ? `${payment.customer.firstName} ${payment.customer.lastName}`
                      : payment.customerId?.firstName && payment.customerId?.lastName
                      ? `${payment.customerId.firstName} ${payment.customerId.lastName}`
                      : 'Unknown Customer')
                  }
                  secondary={
                    <>
                      {formatPaymentMethod(payment.paymentMethod)}
                      {payment.createdAt && (
                        <Typography 
                          component="span" 
                          variant="body2" 
                          color="text.secondary"
                          sx={{ display: 'block' }}
                        >
                          {formatDate(payment.createdAt)}
                        </Typography>
                      )}
                    </>
                  }
                />
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                  <Typography variant="body1" fontWeight="bold" sx={{ mb: 1 }}>
                    {formatCurrency(payment.amount, payment.currency)}
                  </Typography>
                  <Chip 
                    label={payment.status} 
                    color={getStatusColor(payment.status)} 
                    size="small" 
                  />
                </Box>
              </ListItem>
            ))}
          </List>
        )}
      </CardContent>
    </Card>
  );
};

export default RecentPayments;